import { useQueryClient } from "@tanstack/react-query";
import { type ErrorComponentProps, Link, useRouter } from "@tanstack/react-router";
import { ArrowLeft, LogOut, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { isAuthed, signOut } from "@/lib/pb";

/**
 * Single error boundary used by every route. Detects PocketBase's
 * `ClientResponseError`s with status 404 and surfaces them as a friendly
 * "not found or no access" page — that 404 can mean either a real
 * not-found OR an RBAC denial (PB hides existence behind the listRule),
 * so we deliberately blur the two for the user.
 *
 * Self-contained: includes its own minimal "Portal" link so it works
 * whether the surrounding app shell is mounted or not (mounted on both
 * `__root.tsx` and `_app.tsx`).
 */
export function RouteErrorBoundary({ error, reset }: ErrorComponentProps) {
  const status = readStatus(error);
  const isNotFound = status === 404;
  const isForbidden = status === 401 || status === 403;
  const router = useRouter();
  const queryClient = useQueryClient();

  // A bare <Link> is not enough here: TanStack Router keeps rendering the
  // route's errorComponent after navigation unless the boundary is reset,
  // so the "back" click appeared to do nothing. Reset + invalidate clears
  // the boundary and re-runs loaders (covering errors thrown by /dashboard
  // itself), then navigate.
  function backToDashboard() {
    reset?.();
    router.invalidate();
    router.navigate({ to: "/dashboard" });
  }

  // Escape hatch for users whose role genuinely has no access to the page
  // (or whose cached auth is stale): sign out so they can re-login. Clears
  // the query cache for the same cross-user reason as _app.tsx's onSignOut.
  async function handleSignOut() {
    await signOut();
    queryClient.clear();
    reset?.();
    toast.success("Signed out");
    router.navigate({ to: "/signin" });
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4 py-12">
      <Card className="max-w-md">
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          {isNotFound || isForbidden ? <Search className="size-10 text-muted-foreground" /> : null}
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {isNotFound ? "Not found or no access" : "Something went wrong"}
            </h1>
            <p className="text-muted-foreground text-sm">
              {isNotFound
                ? "This page either doesn't exist, or your role doesn't have access to it."
                : isForbidden
                  ? "Your session is no longer valid. Sign in again to continue."
                  : "An unexpected error occurred. Try again, or head back."}
            </p>
            {!isNotFound && !isForbidden && import.meta.env.DEV ? (
              <code className="text-destructive bg-muted/40 mt-2 break-all rounded px-2 py-1 text-left text-xs">
                {String((error as Error)?.message ?? error)}
              </code>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {isForbidden ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/signin" search={{}}>
                  <ArrowLeft />
                  Sign in
                </Link>
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={backToDashboard}>
                <ArrowLeft />
                Back to dashboard
              </Button>
            )}
            {isAuthed() ? (
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                <LogOut />
                Log out
              </Button>
            ) : null}
            {!isNotFound && !isForbidden && reset ? (
              <Button variant="default" size="sm" onClick={reset}>
                <RefreshCw />
                Try again
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function readStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  // PocketBase's ClientResponseError exposes `.status` directly; fetch failures
  // wrapped by the SDK may also surface `.response.status`. Read both.
  const e = error as { status?: number; response?: { status?: number } };
  return e.status ?? e.response?.status;
}

/**
 * Adapter for TanStack Router's `notFoundComponent` slot (different prop
 * signature than `errorComponent`). Synthesizes a 404-shaped error so the
 * single `RouteErrorBoundary` UI handles both cases.
 */
export function NotFoundPage() {
  const synthesized = Object.assign(new Error("Not Found"), { status: 404 });
  return <RouteErrorBoundary error={synthesized} reset={() => {}} info={undefined as never} />;
}
